import eruda from "eruda";
declare const ENABLE_ERUDA: boolean;
if (ENABLE_ERUDA) {
  eruda.init()
}
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import PouchDB from "pouchdb-browser";
import { Provider } from "use-pouchdb";

import { assert } from "./utils";
import { SyncManager, SyncManagerContext, type SyncTarget } from "./repl_status";
import App from "./App";

const db = new PouchDB("inv6", {
  revs_limit: 10000,
});

db.setMaxListeners(100);

function readSyncTargets(): SyncTarget[] {
  const raw = localStorage.getItem("externalDbs");
  if (raw != null) {
    try {
      const parsed: unknown = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        const targets: SyncTarget[] = [];
        for (const item of parsed) {
          // item is `any` (from JSON.parse array); we widen each field to unknown for safe narrowing
          const name: unknown = item?.name;
          const url: unknown = item?.url;
          if (typeof name === 'string' && name && typeof url === 'string' && url) {
            targets.push({ name, url });
          }
        }
        if (targets.length > 0) return targets;
      }
    } catch { /* malformed JSON, fall through */ }
  }
  // Backwards compat: migrate old single-URL format
  const oldUrl = localStorage.getItem("externalDb");
  return [{ name: "default", url: oldUrl ?? "http://user:user@127.0.0.1:5984/inv6" }];
}

// PouchDB types the sync error callbacks as `err: {}` but passes null at runtime
// for clean pauses. `instanceof Error` returns false for null so it acts as our
// null guard. For real errors (TypeError from fetch, PouchDB HTTP errors) it
// extracts a human-readable message instead of showing "[object Object]".
function syncErrMsg(err: {}): string | null {
  return err instanceof Error ? (err.message || err.name || 'unknown error') : null;
}

const syncManager = new SyncManager();
const syncTargets = readSyncTargets();

const replications = syncTargets.map(target => {
  const externalDb = new PouchDB(target.url);
  syncManager.setStatus(target.name, "connecting…");
  return PouchDB.sync(db, externalDb, {
    live: true,
    retry: true,
  }).on('change', function (info) {
    console.log('change', target.name, info);
  }).on('paused', function (err) {
    console.log('paused', target.name, err);
    const msg = syncErrMsg(err);
    syncManager.setStatus(target.name, msg != null ? `error: ${msg}` : "paused");
  }).on('active', function () {
    console.log('active', target.name);
    syncManager.setStatus(target.name, "active");
  }).on('denied', function (err) {
    console.log('denied', target.name, err);
    const msg = syncErrMsg(err);
    syncManager.setStatus(target.name, msg != null ? `denied: ${msg}` : "denied");
  }).on('complete', function (info) {
    console.log('complete', target.name, info);
  }).on('error', function (err) {
    console.log('error', target.name, err);
    const msg = syncErrMsg(err);
    syncManager.setStatus(target.name, msg != null ? `error: ${msg}` : "error");
  }).catch((err: unknown) => {
    console.log('promise caught', target.name, err);
    const msg = err instanceof Error ? (err.message || err.name || 'unknown error') : String(err);
    syncManager.setStatus(target.name, `error: ${msg}`);
  });
});

const inv6Debug = { db, syncTargets, replications, syncManager, ENABLE_ERUDA, PouchDB };
declare global { interface Window { inv6: typeof inv6Debug } }
window.inv6 = inv6Debug;

const rootEl = document.getElementById("root");

assert(rootEl != null);

createRoot(rootEl).render(
  <StrictMode>
    <Provider pouchdb={db}>
      <SyncManagerContext.Provider value={syncManager}>
        <App />
      </SyncManagerContext.Provider>
    </Provider>
  </StrictMode>
);
